import "./Home.css";

import { useState, useEffect, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";

import { useFetchHome } from "~/hooks/api/home/useFetchHome";
import { LoadingContext, actionTypes } from "~/context/LoadingContext";

import { Dashboard } from "./Dashboard/Dashboard";
import { EventCompliance } from "./EventCompliance/EventCompliance";
import { Greeting } from "./Greeting/Greeting";
import { Toast } from "~/components/Base/Toast/Toast";

export function Home() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const { isLoading, status, error } = useFetchHome();
  const [fetchErrors, setFetchErrors] = useState([]);
  const [toastMessage, setToastMessage] = useState(null);
  // Captured at mount so the consume effect's state-clearing call to navigate()
  // does not flip this back to false.
  const preventBackOnBrowserRef = useRef(location.state?.preventBackOnBrowser === true);
  const { dispatch } = useContext(LoadingContext);

  // Show a toast once when the user lands on the dashboard with a message
  // passed in router state (e.g. after submitting a form). Clear the router
  // state so a refresh or back-navigation will not re-trigger it.
  useEffect(() => {
    if (location.state?.message) {
      setToastMessage(location.state.message);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state, navigate]);

  // When the caller asked to prevent the browser back button (e.g. after a
  // form submission), push a duplicate history entry so the next back-press
  // fires popstate without leaving the page; the handler re-pushes "/" so
  // forward history is also cleared. The trap lasts as long as the dashboard
  // is mounted; navigating to another page unmounts Home and clears the trap.
  useEffect(() => {
    if (!preventBackOnBrowserRef.current) return;

    window.history.pushState(null, "", window.location.href);

    const onPopState = () => {
      navigate("/");
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [navigate]);

  useEffect(() => {
    setFetchError("home.dashboard", !!error);
  }, [error]);

  useEffect(() => {
    const LOADING_KEY = "home";
    const type = isLoading
      ? actionTypes.ADD_LOADING_ITEM
      : actionTypes.REMOVE_LOADING_ITEM;

    dispatch({
      type,
      key: LOADING_KEY,
    });
  }, [dispatch, isLoading]);

  // Add or remove error defined by key to error list and ensure unique
  const setFetchError = (key, hasError) => {
    setFetchErrors((prevErrors) =>
      hasError
        ? [...new Set([...prevErrors, key])]
        : prevErrors.filter((val) => val !== key)
    );
  };

  if (isLoading) {
    return null;
  }

  return (
    <div className="home">
      <>
        {toastMessage && (
          <div className="home__toast-layer">
            <Toast
              message={t(toastMessage)}
              onDismiss={() => setToastMessage(null)}
            />
          </div>
        )}
        <div className="home__header">
          <Greeting />
            {status && (
              <EventCompliance
                percent={status.compliancePercent}
                missedCount={status.missedEventsCount}
                studyLocked={status.studyLock}
              />
            )}
        </div>
        <Dashboard
          className="home__dashboard"
          setFetchError={setFetchError}
          showFetchError={fetchErrors.length > 0}
          showUnscheduledTile={
            status?.allUnscheduledEventsCount > 0 && !status?.studyLock
          }
          showVideoTile={status?.hasViedocConnectEnabled}
          studyLocked={status?.studyLock}
        />
      </>
    </div>
  );
}
